// Monaco DefinitionProvider for navigational-features Phase 1.
//
// Slice 3 ships only the registration surface — the handler returned `null`
// for every cursor position. Slice 5 wires the table/view dispatch:
// the handler reads the source text + Monaco position, finds the dotted
// identifier at the cursor, looks it up against the OE tree, and on a
// table/view hit resolves to an Object Explorer reveal (see "Probe vs
// navigation" below for where that reveal runs).
//
// Slice 7 wires alias dispatch as a true Monaco `Location` return — F12 on
// `u` in `WHERE u.id` jumps to the `u` in `FROM users u` inline.
//
// Slice 8 wires CTE dispatch as a true Monaco `Location` return — F12 on
// `recent_orders` in `FROM recent_orders` jumps to the start of the matching
// `WITH recent_orders AS (…)` declaration inline (the returned range spans
// the whole declaration, so Peek Definition scopes it and F12 lands on the
// CTE name).
//
// Slice 9 wires routine dispatch as a fallback to table/view: F12 on a
// procedure / function call opens the routine's CREATE script in a new
// query tab (same shape as Slice 5's OE reveal). When the OE tree carries
// multiple routines that share the same name (PG / Oracle overloads), the
// navigation pushes the candidates into `useRoutinePickerStore` and the
// React-mounted `RoutinePickerDialog` renders the picker — selecting a row
// routes through `navigateToRoutine` just like the single-candidate path.
//
// Probe vs navigation (2026-09-24): Monaco also calls `provideDefinition` for
// the Ctrl/Cmd+hover link probe, and nothing in the call tells the two apart.
// Running the table/view, column and routine navigations here moved the OE
// selection and opened tabs on mere hover (and twice on Ctrl+click: probe +
// click). The provider is now side-effect free: those kinds return a
// `jamsql-nav:` location (`definitionNavRegistry.ts`) and the navigation runs
// in the editor opener (`definitionNavigationOpener.ts`), which Monaco only
// calls on a real navigation (F12, Ctrl/Cmd+click, context menu, opening a
// Peek Definition row). Alias / CTE answers stay real in-buffer locations.
//
// PRD: Ralph/NavigationalFeatures/PRD.md § Slice 3 (registration) +
//      Slice 5 (table/view) + Slice 6 (column) + Slice 7 (alias) +
//      Slice 8 (CTE) + Slice 9 (routine).

import type { editor, IRange, languages, Position } from 'monaco-editor';

import type { DatabaseEngineId } from '@/core/engines';

import type { SqlRange } from '../../sql-grammar/model';
import type { CteBinding } from '../../sql-resolver/types';
import { findTableViewBindingAtCursor } from './identifierAtCursor';
import { findColumnBindingAtCursor } from './columnAtCursor';
import { findAliasBindingAtCursor } from './aliasAtCursor';
import { findCteBindingAtCursor } from './cteAtCursor';
import { findRoutineCandidatesAtCursor } from './routineAtCursor';
import { canRevealInObjectExplorer } from './externalNavigation';
import { canNavigateToColumn } from './columnNavigation';
import { canNavigateToRoutine } from './routineNavigation';
import { buildAliasResolver } from './bufferAliasResolver';
import { definitionNavLocation } from './definitionNavRegistry';
import type { DefinitionNavMonacoApi } from './definitionNavRegistry';
import type { DefinitionNavTarget } from './definitionNavTarget';
import { noteInBufferDefinition } from './definitionNavigationOpener';
import { databaseEngineIdToSqlEngine } from '../../workspace/visual-query-editor/edit/livePullSchemaSnapshot';

/** Lazily-resolved document id used as the resolver-service buffer key. The
 *  registration site captures it as a getter so the provider closure stays
 *  valid across document re-keying (e.g. a query tab switching connections). */
export type DocumentIdGetter = () => string | null;

/** Active connection for the editor. Slice 5 needs it to scope the OE-tree
 *  walk to a single connection (multi-connection trees are common). */
export type ConnectionIdGetter = () => string | null;

/** Active database for the editor. Optional — when unset, the OE-tree
 *  walk falls back to engine defaults. */
export type DatabaseGetter = () => string | null;

export type DefinitionProviderDeps = {
  readonly engineId: DatabaseEngineId;
  readonly getDocumentId: DocumentIdGetter;
  readonly getConnectionId?: ConnectionIdGetter;
  readonly getDatabase?: DatabaseGetter;
  /** Answer only for the registering editor's own model. Every mounted
   *  SqlMonaco (all workspace tabs stay mounted) registers a provider for the
   *  same language id and Monaco runs ALL of them per request, each resolving
   *  against its own connection — so without this guard one F12 produced one
   *  navigation per open query tab. Models no SqlMonaco owns (Peek References
   *  previews, plain Monaco SQL editors) get their single responder from
   *  `unownedModelDefinitionProvider.ts`. Omitted there and in unit tests
   *  (answers any model). */
  readonly isOwnModel?: (model: editor.ITextModel) => boolean;
  /** Monaco namespace subset used to mint the `jamsql-nav:` target models.
   *  Without it, out-of-buffer targets (table/view, column, routine) resolve
   *  to no definition. */
  readonly monaco?: DefinitionNavMonacoApi;
};

export function createDefinitionProvider(
  deps: DefinitionProviderDeps,
): languages.DefinitionProvider {
  return {
    provideDefinition: (model, position) => {
      if (deps.isOwnModel && !deps.isOwnModel(model)) return null;
      const result = handleProvideDefinition(deps, model, position);
      if (result?.kind === 'in-buffer') {
        // Counted by the editor opener only if Monaco then jumps here.
        noteInBufferDefinition({ uri: model.uri.toString(), range: result.range, via: result.via });
        return { uri: model.uri, range: result.range };
      }
      if (result?.kind === 'external' && deps.monaco) {
        return definitionNavLocation(deps.monaco, result.target);
      }
      return null;
    },
  };
}

/** Internal handler — exported as a separate signature so unit tests can
 *  drive the dispatch logic against a fake Monaco model + position without
 *  going through the registration plumbing. Pure: it resolves WHERE a
 *  navigation would go and never performs it (see the header note). */
type DispatchOutcome =
  | { readonly kind: 'no-binding' }
  | { readonly kind: 'external'; readonly target: DefinitionNavTarget }
  | { readonly kind: 'in-buffer'; readonly via: 'alias-decl' | 'cte-body'; readonly range: IRange };

export function handleProvideDefinition(
  deps: DefinitionProviderDeps,
  model: Pick<editor.ITextModel, 'getValue' | 'getOffsetAt'>,
  position: Position,
): DispatchOutcome | null {
  // Capability slices 6–9 will reach for the document id here; Slice 5's
  // table/view path doesn't need it. Reading the getter anyway keeps the
  // closure footprint constant and makes the future additive change
  // zero-noise at the registration site.
  //
  // A `getResolverService` dep used to live alongside this — deleted (Plan
  // 19 T9, decision 10): shipped capability slices 5–9 all found other
  // paths (OE tree, alias/CTE/routine cursor scanners) and never read it;
  // both providers only ever `void`-called the getter. Reviving per-
  // connection resolver-service threading here would be wiring a dep to
  // zero readers.
  void deps.getDocumentId();

  const source = model.getValue();
  const offset = model.getOffsetAt(position);

  // CTE dispatch (Slice 8) — pure in-buffer navigation, no connection or OE
  // tree needed. Runs before alias dispatch because the alias scanner can
  // also surface a `source: 'cte'` binding for `FROM cte` references; we
  // want F12 on a CTE name to jump to its WITH-clause body, not to an
  // alias-shaped self-reference. Multi-part chains never reach this path
  // (the scanner only matches 1-part identifiers).
  //
  // Range covers the whole `name AS (…)` declaration — Peek Definition
  // highlights exactly the range we return, so handing back a 0-width point
  // (as this used to) renders a peek with nothing scoped, and handing back
  // only `bodyRange` drops the CTE's name from the highlight.
  //
  // Exception: when the cursor already sits inside that declaration (a
  // recursive CTE's self-reference) the range collapses to a 0-width point at
  // its start. Monaco tests `references.referenceAt(uri, position)` before
  // navigating (`goToCommands.js:111-131`): when the single result CONTAINS
  // the cursor it reads that as "already at the definition" and runs the
  // configured *alternative* command instead of navigating — that's
  // `gotoLocation.alternativeDefinitionCommand`, which defaults to
  // `editor.action.goToReferences` (`editorOptions.js:700`). So a wide range
  // here would make F12 on the inner `cte` reference pop the references peek
  // instead of jumping to the declaration.
  const cteHit = findCteBindingAtCursor(source, offset, { engineId: deps.engineId });
  if (cteHit) {
    return {
      kind: 'in-buffer',
      via: 'cte-body',
      range: cteDefinitionRange(cteHit.binding.target, offset),
    };
  }

  // Alias dispatch (Slice 7) — pure in-buffer navigation, no connection or OE
  // tree needed. Runs after CTE dispatch so CTE names win over any
  // alias-shaped CTE reference. Cursor on the alias's own declaration is a
  // documented no-op (already at the definition).
  const aliasHit = findAliasBindingAtCursor(source, offset, { engineId: deps.engineId });
  if (aliasHit) {
    if (aliasHit.isOnDeclaration) return { kind: 'no-binding' };
    return {
      kind: 'in-buffer',
      via: 'alias-decl',
      range: sqlRangeToMonacoRange(aliasHit.binding.target.range),
    };
  }

  // Remaining dispatches (column / table / view) need a connection because
  // they look up the OE tree.
  const connectionId = deps.getConnectionId?.() ?? null;
  if (!connectionId) return { kind: 'no-binding' };

  const database = deps.getDatabase?.() ?? null;

  // Column nav (Slice 6) wins over table/view nav (Slice 5) when the cursor
  // is on the rightmost segment of a 2-part `users.email` chain — otherwise
  // F12 on `email` would silently navigate to the table `users` and miss the
  // column. We only run the column path when the chain has 2+ parts; bare
  // 1-part identifiers fall through to the table/view path.
  //
  // Alias-prefixed columns (`u.email` where `u` is `FROM users u`) are
  // resolved by the lazily-built buffer-scoped alias resolver. The resolver
  // re-parses the buffer on first use only — F12 on an unqualified or table-
  // qualified column never pays for the parse.
  const columnHit = findColumnBindingAtCursor(source, offset, {
    engineId: deps.engineId,
    connectionId,
    database,
    resolveAlias: buildAliasResolver({
      source,
      cursorOffset: offset,
      engine: databaseEngineIdToSqlEngine(deps.engineId) ?? 'mssql',
    }),
  });
  if (columnHit) {
    const target = {
      relation: columnHit.binding.relation.qualified,
      column: columnHit.binding.column,
      relationKind: columnHit.relationKind,
      engineId: deps.engineId,
      connectionId,
      database: database ?? undefined,
    };
    if (!canNavigateToColumn(target)) return { kind: 'no-binding' };
    return { kind: 'external', target: { via: 'column-target', target } };
  }

  const binding = findTableViewBindingAtCursor(source, offset, {
    engineId: deps.engineId,
    connectionId,
    database,
  });
  if (binding && (binding.kind === 'table' || binding.kind === 'view')) {
    const target = {
      objectKind: binding.kind,
      qualified: binding.target,
      engineId: deps.engineId,
      connectionId,
      database: database ?? undefined,
    };
    if (!canRevealInObjectExplorer(target)) return { kind: 'no-binding' };
    return { kind: 'external', target: { via: 'object-explorer', target } };
  }

  // Routine dispatch (Slice 9) — fallback after table/view dispatch. Same
  // OE-tree-as-SchemaSnapshot pattern, looking up `stored-procedure` /
  // `function` nodes. Putting it last preserves the user-visible "F12 on
  // `users` opens the table" behavior even when a routine happens to share
  // a name; the much-more-common routine case (`EXEC my_proc`,
  // `SELECT my_func()`) sees no table/view hit and falls through here.
  //
  // Overload preview (Slice 9 finish): when the OE tree carries 2+ matching
  // routines (PG / Oracle overloads), open the `RoutinePickerDialog` so the
  // user picks one. MSSQL / MySQL produce at most one match because they
  // don't support overloading; in those cases the candidate list collapses
  // to a single entry and we navigate directly without showing the picker.
  const routineCandidates = findRoutineCandidatesAtCursor(source, offset, {
    engineId: deps.engineId,
    connectionId,
    database,
  });
  if (routineCandidates.length >= 2) {
    const context = { engineId: deps.engineId, connectionId, database: database ?? undefined };
    return { kind: 'external', target: { via: 'routine-picker', candidates: routineCandidates, context } };
  }
  const routineHit = routineCandidates[0];
  if (routineHit) {
    const target = {
      qualified: routineHit.binding.target,
      routineKind: routineHit.routineKind,
      engineId: deps.engineId,
      connectionId,
      database: database ?? undefined,
      objectId: routineHit.objectId,
    };
    if (!canNavigateToRoutine(target)) return { kind: 'no-binding' };
    return { kind: 'external', target: { via: 'routine-target', target } };
  }

  return { kind: 'no-binding' };
}

// SqlRange line/column are 0-indexed; Monaco IRange is 1-indexed. Mirror the
// convention used in the code-action provider boundary (Slice 4).
function sqlRangeToMonacoRange(range: SqlRange): IRange {
  return {
    startLineNumber: range.startLine + 1,
    startColumn: range.startColumn + 1,
    endLineNumber: range.endLine + 1,
    endColumn: range.endColumn + 1,
  };
}

// 0-width Monaco IRange anchored at the SqlRange's start. Used when the
// navigation target is a region that may contain the cursor (e.g. a recursive
// CTE's own declaration): Monaco's `referenceAt` containment check makes F12
// run the alternative command (`editor.action.goToReferences`) instead of
// navigating when the sole result contains the cursor, so we hand back a
// point outside the cursor's position and let the navigation happen.
function sqlRangeStartPoint(range: SqlRange): IRange {
  const line = range.startLine + 1;
  const col = range.startColumn + 1;
  return { startLineNumber: line, startColumn: col, endLineNumber: line, endColumn: col };
}

/** Navigation target for a CTE reference: the whole `name AS (…)` declaration
 *  (`nameRange.start` → `bodyRange.end`), collapsed to a 0-width point at its
 *  start when the cursor is already inside it (recursive self-reference).
 *  Falls back to `bodyRange` alone for bindings built without a `nameRange`. */
function cteDefinitionRange(cte: CteBinding, cursorOffset: number): IRange {
  const start = cte.nameRange ?? cte.bodyRange;
  const declaration: SqlRange = {
    ...cte.bodyRange,
    startOffset: start.startOffset,
    startLine: start.startLine,
    startColumn: start.startColumn,
  };
  const cursorInside =
    cursorOffset >= declaration.startOffset && cursorOffset <= declaration.endOffset;
  return cursorInside ? sqlRangeStartPoint(declaration) : sqlRangeToMonacoRange(declaration);
}
